import {
  computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { ALLERGY_OPTIONS, ALLERGY_OPTIONS_CN, CURRENT_MEDICINE_TAKEN_OPTIONS, CURRENT_MEDICINE_TAKEN_OPTIONS_CN, LANDING_PAGE, MODALITY, PAST_DISEASE_OPTIONS, PAST_DISEASE_OPTIONS_CN, SYMPTOM_OPTIONS, SYMPTOM_OPTIONS_CN, TRY_GUIDED_MASSAGE_UI, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM, isBuild, VERSION_NO } from '../constants/settings';
import { Gender } from '../constants/options';
import { ProfileTemplate, TimeSlotStyle } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, LangList, Language } from '../utilities/general';
import { PackageFeatures, PACKAGE_END_DATA_NULL_PLACEHOLDER } from '../utilities/package-subscription';
import { FAxios } from '../utilities/file-network';
import {
  AiConversationTranscription, AiHerbTranslation, AiSummaryResponse, AiSummarySupremeResult,
  AiMultiTargetLangRequest, AiTargetLang, AiTranslateRawResponse, AiTranslateRequest, AiTranslateResult,
  NormalizeTranslation, ParseSummaryAndTranslate,
} from '../constants/ai';
import { t } from 'i18next';
import useStores from '../hooks/use-stores';
import { NavUrl } from "../components/shared-layout";

/**
 * User base information
 */
export interface UserInfo {
  id: number,
  username: string,
  firstName: string,
  lastName: string,
  email?: string,
  phone?: string,
  templateId?: ProfileTemplate | number,
  gender: Gender,
  availableClinics: any[],
  currentClinicId: number,
  isPublished: boolean,
  packagePermissions: any,
  // The signed-in user's own effective RBAC permission codes + role for
  // currentClinicId (e.g. "booking.all.view") — see utilities/permission-role.ts.
  // Populated by selectClinic's response (permissions/role) and refreshed by
  // clinicStore.getMyPermissions (periodic poll + on 403). Kept in sync with
  // UserStore.permissionCodes/currentRoleInfo (the observable, reactive copy)
  // via UserStore.setPermissionCodes — this field is the reload-durable copy.
  permissionCodes?: string[],
  currentRoleInfo?: any,
  profileImageSavedName?: string,
  slug?: string;
  timeSlotStyle?: TimeSlotStyle;
  lastLoginAt?: string;
  cmbaNumber?: string;
  cmbaStartDate?: string;
  cmbaEndDate?: string;
  entryPage?: NavUrl | any,
}

export enum PackageActionType {
  NONE,
  ADD,
  ADD_FROM_CLINIC,
  RENEW,
  UPGRADE,
  EXTEND,
}

export default class UserStore {
  get isAuthed() {
    return isWeb && localStorage.USER_TOKEN != null && localStorage.USER_INFO != null;
  }

  set isAuthed(value) {
    console.log('Deleting token...');
    // if (!value && isWeb) localStorage.clear();
    const landingPage = localStorage.getItem(LANDING_PAGE); // 临时保存
    const lang = localStorage.LANG; // 临时保存
    const i18nextLang = localStorage.i18nextLng; // 临时保存
    const cmbaWarning = localStorage.CMBA_WARNING; // 临时保存
    const insuranceWarning = localStorage.INSURANCE_WARNING; // 临时保存
    const configureProductWarning = localStorage.CONFIGURE_PRODUCT_WARNING;
    const tryGuidedUI = localStorage.getItem(TRY_GUIDED_MASSAGE_UI); // 临时保存
    localStorage.clear();                                     // 清空所有
    if (landingPage) localStorage.setItem(LANDING_PAGE, landingPage);      // 恢复
    if (lang) localStorage.LANG = lang;                             // 恢复
    if (i18nextLang) localStorage.i18nextLng = i18nextLang;                // 恢复
    if (cmbaWarning) localStorage.CMBA_WARNING = cmbaWarning;                // 恢复
    if (insuranceWarning) localStorage.INSURANCE_WARNING = insuranceWarning;                // 恢复
    if (configureProductWarning) localStorage.CONFIGURE_PRODUCT_WARNING = configureProductWarning
    if (tryGuidedUI) localStorage.setItem(TRY_GUIDED_MASSAGE_UI, tryGuidedUI);      // 恢复
  }

  loading: boolean = false;

  loadingAccount: boolean = false;

  verifyingOutsider: boolean = false;

  gettingSummary: boolean = false;

  voiceToText: boolean = false;


  signingIn: boolean = false;

  initingClinic: boolean = false;

  userToken: string | null = null;

  // The signed-in user's own RBAC permission codes + role for currentClinicId
  // — observable so any `observer` component calling HasPermission() (see
  // utilities/permission-role.ts) re-renders the instant these change, instead
  // of only picking up fresh values on their next unrelated re-render. Hydrated
  // from USER_INFO on construction (see below) so a page reload doesn't start
  // blank; kept in sync with it afterwards by setPermissionCodes.
  permissionCodes: string[] = [];
  currentRoleInfo: any = null;

  // Scoped, non-persisted permission/token override for viewing/editing a
  // clinic that ISN'T the user's real currentClinicId (clinic/info.tsx and
  // clinic/products/index.tsx, see selectClinic's isTemp param below) — never
  // written to localStorage.USER_TOKEN/USER_INFO, so the user's real session
  // permissions for their actual current clinic are never touched. Consumed
  // by HasPermission (utilities/permission-role.ts) and the Axios/FAxios
  // request interceptors, both of which check isTempPermissionActive first.
  // Pages that activate this MUST call exitTempPermissionMode on unmount —
  // it's a global flag, so anything left active would leak into whatever the
  // user navigates to next.
  isTempPermissionActive: boolean = false;
  tempPermissionCodes: string[] = [];
  tempToken: string = "";

  // USER_INFO.currentClinicId/availableClinics are plain localStorage, not
  // MobX state — writing them (e.g. PractitionerStore.fetchRelatedClinics
  // auto-assigning a member's first clinic as currentClinicId once one
  // appears) doesn't make any already-mounted `observer` component
  // re-render, so nav/menu gating built on GetUserProfile() can silently
  // keep showing the "no clinic" state. Bumping this observable alongside
  // that kind of write gives components a signal to react to — see
  // bumpUserInfoVersion.
  userInfoVersion: number = 0;

  isVersionChanged: boolean = false;
  gettingVersion: boolean = false;

  translating: boolean = false;
  gettingToken: boolean = false;

  // openPackageWindow: boolean = false;

  defaultPackageItem: any = null;
  defaultPackageClinicId: number = 0;

  resetPasswordLoading: boolean = false;


  packageActionType: PackageActionType = PackageActionType.NONE;
  isPackageGuideOpen: boolean = false;
  isPackageAutoRechargeEnabled: boolean = true

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      signingIn: observable,
      initingClinic: observable,
      verifyingOutsider: observable,
      translating: observable,
      gettingSummary: observable,
      defaultPackageItem: observable,
      packageActionType: observable,
      defaultPackageClinicId: observable,
      voiceToText: observable,
      isVersionChanged: observable,
      gettingVersion: observable,
      resetPasswordLoading: observable,
      loadingAccount: observable,
      gettingToken: observable,
      isPackageGuideOpen: observable,
      permissionCodes: observable,
      currentRoleInfo: observable,
      isTempPermissionActive: observable,
      tempPermissionCodes: observable,
      tempToken: observable,
      userInfoVersion: observable,
    });
    this.rootStore = rootStore;
    if (isWeb) {
      const userInfo = GetUserProfile();
      this.permissionCodes = userInfo?.permissionCodes ?? [];
      this.currentRoleInfo = userInfo?.currentRoleInfo ?? null;
    }
  }

  // Single source of truth for "my RBAC permissions changed" — updates the
  // observable (for reactivity) and persists onto USER_INFO (for reload
  // durability), merging onto whatever is currently there rather than a
  // caller's possibly-stale snapshot.
  setPermissionCodes = (codes: string[], role?: any) => {
    this.permissionCodes = codes ?? [];
    if (role) this.currentRoleInfo = role;
    const userInfo = GetUserProfile();
    if (!userInfo) return;
    userInfo.permissionCodes = this.permissionCodes;
    if (role) userInfo.currentRoleInfo = role;
    SetLocalStorage(USER_INFO, userInfo);
  };

  // Call after writing USER_INFO.currentClinicId/availableClinics OUTSIDE
  // this store (e.g. PractitionerStore.fetchRelatedClinics auto-assigning a
  // first clinic) so any mounted `observer` reading this — see
  // shared-layout.tsx/mini-layout.tsx's Layout — re-renders and picks up
  // the fresh GetUserProfile() snapshot instead of only catching up on its
  // next unrelated re-render.
  bumpUserInfoVersion = () => {
    this.userInfoVersion++;
  };

  onOpenPackageWindow = (type: PackageActionType, defaultPackageItem: any = null, defaultPackageClinicId: number = 0, isGuideOn: boolean = false) => {
    // this.openPackageWindow = true;
    if (defaultPackageClinicId === 0) {
      const currentClinicId = GetUserProfile()?.currentClinicId ?? 0;
      const currentMyClinic = GetUserProfile().availableClinics.filter(((c: any) => c.isOwner)).find((c: any) => c.id == currentClinicId);
      if (currentMyClinic) {
        this.defaultPackageClinicId = currentClinicId;
      } else {
        this.defaultPackageClinicId = 0;
      }
    } else {
      this.defaultPackageClinicId = defaultPackageClinicId;
    }

    this.packageActionType = type;
    this.defaultPackageItem = defaultPackageItem;
    if (isGuideOn) this.isPackageGuideOpen = true;
  };


  onChangeIsPackageAutoRechargeEnabled = (isEnabled: boolean) => {
    this.isPackageAutoRechargeEnabled = isEnabled;
  }

  onClosePackageWindow = () => {
    // this.openPackageWindow = false;
    // setTimeout(() => {
    this.packageActionType = PackageActionType.NONE;
    this.defaultPackageItem = null;
    this.defaultPackageClinicId = 0;
    this.isPackageAutoRechargeEnabled = true;
    // }, 500);
  };

  signIn = async (userModel: any) => {
    this.signingIn = true;
    try {
      const res = await Axios.post(`/api/Auth/SignIn?lang=${GetApiLang()}&platformId=${CURRENT_PLATFORM}`, userModel);
      const token = `Bearer ${res.data.token}`;
      if (!isWeb) return;
      const user = res.data;
      this.userToken = user.token;
      localStorage.USER_TOKEN = token;
      localStorage.IS_TEMP_TOKEN = true;
      let userInfo: UserInfo = {
        id: user.id,
        username: user.username,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        templateId: user.templateId,
        gender: user.gender,
        availableClinics: user.availableClinics,
        slug: user.slug,
        currentClinicId: user.availableClinics.length == 1 ? user.availableClinics[0].id : 0,
        isPublished: user.isPublished,
        profileImageSavedName: user.profileImageSavedName,
        timeSlotStyle: user.timeSlotStyle,
        lastLoginAt: user.lastLoginAt,
        cmbaNumber: user.cmbaNumber,
        cmbaStartDate: user.cmbaStartDate,
        cmbaEndDate: user.cmbaEndDate,
        entryPage: user.entryPage,
        packagePermissions: {
          features: [PackageFeatures.BASIC],
          endDate: PACKAGE_END_DATA_NULL_PLACEHOLDER,
          clinicPackages: [],
          id: 0,
        },
      };
      SetLocalStorage(USER_INFO, userInfo);
      console.log("User Info", res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      this.isAuthed = false;
      return Promise.reject(err);
    } finally {
      this.signingIn = false;
    }
  };

  signInWithOrg = async (orgToken: string) => {
    this.signingIn = true;
    try {
      const res = await Axios.post(`/api/Auth/Institution/SignIn?lang=${GetApiLang()}&token=${orgToken}&platformId=${CURRENT_PLATFORM}`);
      const token = `Bearer ${res.data.token}`;
      const user = res.data;
      this.userToken = user.token;
      if (isWeb) {
        localStorage.USER_TEMP_TOKEN = token;
        let userInfo: UserInfo = {
          id: user.id,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          templateId: user.templateId,
          gender: user.gender,
          availableClinics: user.availableClinics,
          slug: user.slug,
          currentClinicId: user.availableClinics.length == 1 ? user.availableClinics[0].id : 0,
          isPublished: user.isPublished,
          packagePermissions: {
            features: [PackageFeatures.BASIC],
            endDate: "2077-08-20T00:00:00",
            clinicPackages: [],
          },
        };
        SetLocalStorage(USER_INFO, userInfo);
      }
      console.log("User Info", res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      this.isAuthed = false;
      return Promise.reject(err);
    } finally {
      this.signingIn = false;
    }
  };

  // Response now carries this clinic's RBAC data alongside the token:
  // { memberId, clinicId, permissions: string[], role: RoleInfo, token }.
  //
  // isTemp: for viewing/editing a clinic that isn't the user's real
  // currentClinicId (clinic/info.tsx, clinic/products/index.tsx) — the
  // response is kept ONLY in the non-persisted tempPermissionCodes/tempToken
  // fields (never written to localStorage.USER_TOKEN/USER_INFO, never fed
  // through setPermissionCodes), so the user's actual session permissions for
  // their real current clinic are completely untouched. The caller is
  // responsible for calling exitTempPermissionMode when leaving that page.
  selectClinic = async (clinicId: number, isTemp: boolean = false) => {
    this.initingClinic = true;
    try {
      const res = await Axios.post(`/api/Auth/Select/Clinic?platformId=${CURRENT_PLATFORM}`, { clinicId, isTemp });
      if (isWeb) {
        if (isTemp) {
          this.tempToken = `Bearer ${res.data.token}`;
          this.tempPermissionCodes = res.data.permissions ?? [];
          this.isTempPermissionActive = true;
        } else {
          localStorage.USER_TOKEN = `Bearer ${res.data.token}`;
          this.userToken = `Bearer ${res.data.token}`;
          this.setPermissionCodes(res.data.permissions ?? [], res.data.role);
        }
      }
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.initingClinic = false;
    }
  };

  // Must be called when leaving clinic/info.tsx or clinic/products/index.tsx
  // (e.g. an unmount effect) — isTempPermissionActive is a global flag read
  // by HasPermission and both Axios/FAxios interceptors, so leaving it set
  // would silently apply this clinic's temp permissions/token to whatever
  // page the user navigates to next.
  exitTempPermissionMode = () => {
    this.isTempPermissionActive = false;
    this.tempPermissionCodes = [];
    this.tempToken = "";
  };

  // Guards against the session timing out mid-transaction while a Hicaps
  // terminal call is actually in flight (a real card swipe/claim can take a
  // while) — called with true right as a Hicaps payment/refund dialog opens,
  // and false as soon as it closes, however it closes (success, cancel, or
  // unmount), so protection never stays on longer than the dialog is open.
  setSignInProtection = async (enableSignInProtection: boolean) => {
    try {
      const res = await Axios.put(`/api/Auth/SignIn/Protection?enableSignInProtection=${enableSignInProtection}`);
      return Promise.resolve(res.data);
    } catch (e: any) {
      return Promise.reject(e);
    }
  };

  signOut = async (isRedirect: boolean = true) => {
    this.signingIn = true;
    try {
      const res = await Axios.post(`/api/Auth/SignOut?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (e: any) {
      return Promise.reject(e);
    } finally {
      this.signingIn = false;
      this.isAuthed = false;
      this.userToken = null;
      if (!isRedirect) return;
      navigate('/');
      const { practitionerStore } = useStores();
      practitionerStore.pendingCount = 0;
      this.rootStore.notify(t('BYE'), "success");
    }
  };

  signOutLite = async () => {
    this.signingIn = true;
    try {
      const res = await Axios.post(`/api/Auth/SignOut?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (e: any) {
      return Promise.reject(e);
    } finally {
      this.signingIn = false;
      this.isAuthed = false;
      this.userToken = null;
      navigate('/');
    }
  };


  signUp = async (userModel: any, lang = GetLangKey()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Auth/SignUp?lang=${lang}&platformId=${CURRENT_PLATFORM}`, userModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  signUpTokenCheck = async (token: string) => {
    this.verifyingOutsider = true;
    try {
      const res = await Axios.post(`/api/Auth/SignUp/InviteToken/Check?token=${token}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.verifyingOutsider = false;
    }
  };

  signUpWithInviteToken = async (userModel: any, lang = GetLangKey()) => {
    this.loading = true;
    try {
      const token = userModel.inviteToken;
      const res = await Axios.post(`/api/Auth/SignUp/InviteToken?token=${token}&lang=${lang}&platformId=${CURRENT_PLATFORM}`, userModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  checkAgentRefKey = async (key: string) => {
    this.verifyingOutsider = true;
    try {
      const res = await Axios.post(`/api/Auth/Check/AgentRefKey?key=${key}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.verifyingOutsider = false;
    }
  };

  getSignUpInfoFromOrg = async (orgToken: string) => {
    this.verifyingOutsider = true;
    try {
      const res = await Axios.get(`/api/Auth/Institution/SignUp?token=${orgToken}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.verifyingOutsider = false;
    }
  };

  /**
   * The API now answers with `{ isSuccess, response: { ..., response: [{ lang, content }] } }`.
   * Flattened here so callers keep reading `res.response` as plain text.
   */
  translateTo = async (reqModel: AiMultiTargetLangRequest): Promise<AiTranslateResult> => {
    this.translating = true;
    try {
      const res = await Axios.post<AiTranslateRawResponse>(`/api/Translate?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(NormalizeTranslation(res.data, reqModel.targetLang[0]));
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.translating = false;
    }
  };

  // translateToByToken = async (reqModel: AiTranslateRequest, token: any): Promise<AiTranslateResult> => {
  //   this.translating = true;
  //   try {
  //     const res = await Axios.post<AiTranslateRawResponse>(`/api/Translate/Token?token=${token}&clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, { ...reqModel });
  //     return Promise.resolve(NormalizeTranslation(res.data, reqModel.targetLang));
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.translating = false;
  //   }
  // };

  // /**
  //  * @deprecated Use {@link translateHerb} / {@link translateHerbBulk}, which hit
  //  * `/api/Translate/Herb/Bulk`. This endpoint splits the model output on commas,
  //  * so a name containing a comma silently shifts pinYin/latin into the wrong fields.
  //  */
  // translateHerbLegacy = async (reqModel: any): Promise<AiHerbTranslation> => {
  //   this.translating = true;
  //   try {
  //     if (!isBuild) this.rootStore.notify("Deprecated API Request", "warning");
  //     const res = await Axios.post<AiHerbTranslation>(`/api/Translate/Herb?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, { ...reqModel });
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.translating = false;
  //   }
  // };

  /**
   * Herb / acupoint name translation. Takes the whole list in one call and returns
   * it back in the SAME ORDER, with only the BLANK fields filled in — names already
   * present are left untouched. Known herbs are resolved straight from the herb
   * table (no AI call, no cost); only the leftovers go to the model.
   */
  translateHerbBulk = async (herbs: AiHerbTranslation[]): Promise<AiHerbTranslation[]> => {
    if (herbs.length == 0) return Promise.resolve([]);
    this.translating = true;
    try {
      const res = await Axios.post<AiHerbTranslation[]>(`/api/Translate/Herb/Bulk?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, herbs);
      return Promise.resolve(res.data ?? []);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.translating = false;
    }
  };

  /**
   * Single-item convenience wrapper over {@link translateHerbBulk} — the
   * "AI Auto Translate" button in the herb / formula / acupoint modals.
   * Falls back to the request itself if the API returns nothing, so callers
   * never have to null-check the row.
   */
  translateHerb = async (reqModel: AiHerbTranslation): Promise<AiHerbTranslation> => {
    const res = await this.translateHerbBulk([{
      nameEn: reqModel.nameEn ?? null,
      nameCn: reqModel.nameCn ?? null,
      pinYin: reqModel.pinYin ?? null,
      latin: reqModel.latin ?? null,
    }]);
    return res[0] ?? reqModel;
  };

  getAIsummary = async (reqModel: { content: string; }): Promise<AiSummaryResponse> => {
    this.gettingSummary = true;
    try {
      const res = await Axios.post<AiSummaryResponse>(`/api/Summary?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.gettingSummary = false;
    }
  };

  getAIsummaryByToken = async (reqModel: { content: string; }, token: string): Promise<AiSummaryResponse> => {
    this.gettingSummary = true;
    try {
      const res = await Axios.post<AiSummaryResponse>(`/api/Summary/Token?token=${token}&clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, { ...reqModel });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.gettingSummary = false;
    }
  };

  // /**
  //  * @deprecated Use Azure Function instead, this function uses Elevenlabs API
  //  */
  // getAIVoice2text = async (reqModel: any) => {
  //   this.voiceToText = true;
  //   try {
  //     if (!isBuild) this.rootStore.notify("Deprecated API Request", "warning");

  //     const res = await FAxios.post(`/api/Translate/Voice?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.voiceToText = false;
  //   }
  // };

  // /**
  //  * @deprecated Use Azure Function instead, this function uses Elevenlabs API
  //  */
  // getAIVoice2textByToken = async (reqModel: any, token: string) => {
  //   this.voiceToText = true;
  //   try {
  //     if (!isBuild) this.rootStore.notify("Deprecated API Request", "warning");
  //     const res = await FAxios.post(`/api/Translate/Voice/Token?token=${token}&clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.voiceToText = false;
  //   }
  // };

  getAIVoice2textAzure = async (reqModel: any): Promise<AiConversationTranscription> => {
    this.voiceToText = true;
    try {
      const res = await FAxios.post<AiConversationTranscription>(`/api/Translate/Voice/Azure?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.voiceToText = false;
    }
  };

  getAIVoice2textByTokenAzure = async (reqModel: any, token: string) => {
    this.voiceToText = true;
    try {
      const res = await FAxios.post<AiConversationTranscription>(`/api/Translate/Voice/Azure/QR?token=${token}&clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.voiceToText = false;
    }
  };

  // /**
  //  * @deprecated Renamed to `/api/Summary/Supreme` — use {@link getAISummarySupreme}.
  //  */
  // getAISummaryAdvanced = async (clinicId: number, reqModel: { targetLang: AiTargetLang | string, content: string; }) => {
  //   this.gettingSummary = true;
  //   try {
  //     if (!isBuild) this.rootStore.notify("Deprecated API Request", "warning");
  //     const res = await Axios.post(`/api/Summary/Advanced?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.gettingSummary = false;
  //   }
  // };

  /** Translate and summarise into one `Language`-coded response list. */
  getAISummaryAndTranslate = async (clinicId: number, reqModel: AiMultiTargetLangRequest): Promise<AiSummarySupremeResult> => {
    this.gettingSummary = true;
    try {
      const res = await Axios.post(`/api/Summary/Supreme?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve({
        isSuccess: res.data?.isSuccess ?? true,
        response: ParseSummaryAndTranslate(res.data),
        raw: res.data,
      });
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.gettingSummary = false;
    }
  };

  /** Same summary + translation operation for QR/mobile sessions. */
  getAISummaryAndTranslateByToken = async (reqModel: AiMultiTargetLangRequest, token: string): Promise<AiSummarySupremeResult> => {
    this.gettingSummary = true;
    try {
      const res = await Axios.post(`/api/Summary/Supreme/Token?token=${token}&platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve({
        isSuccess: res.data?.isSuccess ?? true,
        response: ParseSummaryAndTranslate(res.data),
        raw: res.data,
      });
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.gettingSummary = false;
    }
  };

  searchModality = async (reqModel: any = {}) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Modality/Search/0/0?platformId=${CURRENT_PLATFORM}`, reqModel);
      SetLocalStorage(MODALITY, res.data.items);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchSymptomsOptions = async (reqModel: any = {}, lang: Language = GetApiLang()) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Option/Symptom/Search?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
      if (lang == Language.ENGLISH) SetLocalStorage(SYMPTOM_OPTIONS, res.data);
      else SetLocalStorage(SYMPTOM_OPTIONS_CN, res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  resetPasswordSendSMS = async (reqModel: any) => {
    this.resetPasswordLoading = true;
    try {
      const res = await Axios.post(`/api/Auth/Password/SendCode?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.resetPasswordLoading = false;
    }
  };

  resetPasswordResetWithSMS = async (reqModel: any) => {
    this.resetPasswordLoading = true;
    try {
      const res = await Axios.post(`/api/Auth/Password/Reset?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.resetPasswordLoading = false;
    }
  };

  // get11LabsOneTimeToken = async () => {
  //   this.gettingToken = true;
  //   try {
  //     const res = await Axios.post(`/api/Translate/Voice/Live?clinicId=${GetUserProfile()?.currentClinicId}&platformId=${CURRENT_PLATFORM}`, {});
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.gettingToken = false;
  //   }
  // };

  // get11LabsOneTimeTokenByPhoneToken = async (phoneToken: string) => {
  //   this.gettingToken = true;
  //   try {
  //     const res = await Axios.post(`/api/Translate/Voice/Live/Token?token=${phoneToken}&platformId=${CURRENT_PLATFORM}`, {});
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.gettingToken = false;
  //   }
  // };

  // uploadLiveScribeInfo = async (reqModel: any, clinicId: number) => {
  //   this.gettingToken = true;
  //   try {
  //     const res = await FAxios.post(`/api/Translate/Voice/Live/File?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`, reqModel);
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.gettingToken = false;
  //   }
  // };

  // uploadLiveScribeInfoByPhoneToken = async (reqModel: any, token: string) => {
  //   this.gettingToken = true;
  //   try {
  //     const res = await FAxios.post(`/api/Translate/Voice/Live/File/Token?token=${token}&platformId=${CURRENT_PLATFORM}`, reqModel);
  //     return Promise.resolve(res.data);
  //   } catch (err) {
  //     return Promise.reject(err);
  //   } finally {
  //     this.gettingToken = false;
  //   }
  // };

  checkUsername = async (username: string) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Auth/Check/Username?platformId=${CURRENT_PLATFORM}`, { username });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getVersion = async (platformId: number = CURRENT_PLATFORM) => {
    if (this.gettingVersion) return;
    this.gettingVersion = true;
    this.isVersionChanged = false;
    try {
      const res = await Axios.get(`/api/System/Version/${platformId}?platformId=${CURRENT_PLATFORM}`);
      if (localStorage.VERSION_WARNING !== VERSION_NO) {
        if (VERSION_NO !== res.data.frontEndNo) this.isVersionChanged = true;
      };
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.gettingVersion = false;
    }
  };

  updateEntryPage = async (entryPage: NavUrl) => {
    this.loadingAccount = true;
    try {
      const res = await Axios.put(`/api/Practitioner/EntryPage?entryPage=${entryPage}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccount = false;
    }
  };

  promotionSearch = async () => {
    this.loadingAccount = true;
    try {
      const res = await Axios.post(`/api/Auth/Promotion/Search?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccount = false;
    }
  };

  translateLangByEn = async (reqModel: { nameEn: string }) => {
    this.translating = true;
    try {
      const req: AiMultiTargetLangRequest = {
        targetLang: LangList.filter((l) => l !== Language.ENGLISH),
        content: reqModel.nameEn
      };
      const res = await Axios.post(`/api/Translate/?clinicId=${GetUserProfile()?.currentClinicId}&clinicplatformId=${CURRENT_PLATFORM}`, req);
      const translations = ParseSummaryAndTranslate(res.data);
      const getContent = (lang: Language) => translations.find((item) => item.lang === lang)?.content ?? "";
      return Promise.resolve({
        nameEn: reqModel.nameEn,
        nameCn: getContent(Language.ZH_CN),
        nameTh: getContent(Language.TH_TH),
        nameVn: getContent(Language.VI_VN),
      });
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.translating = false;
    }
  };

}
